import AsyncStorage from '@react-native-async-storage/async-storage'
import * as StoreReview from 'expo-store-review'
import { Linking, Platform } from 'react-native'

const RATED_KEY = '@dateability/review-rated'
const LAST_PROMPTED_AT_KEY = '@dateability/review-last-prompted-at'
const PROMPT_INTERVAL_MS = 30 * 24 * 60 * 60 * 1000

const APP_STORE_REVIEW_URL = 'https://apps.apple.com/app/id6443474660?action=write-review'
const PLAY_STORE_MARKET_URL = 'market://details?id=com.dateabilityapp'
const PLAY_STORE_WEB_URL = 'https://play.google.com/store/apps/details?id=com.dateabilityapp'

export type ReviewTrigger = 'match' | 'likes'

export interface ReviewState {
  rated: boolean
  lastPromptedAt: number | null
}

export const loadReviewState = async (): Promise<ReviewState> => {
  const values = await AsyncStorage.multiGet([RATED_KEY, LAST_PROMPTED_AT_KEY])
  const map = Object.fromEntries(values)
  const lastPromptedRaw = map[LAST_PROMPTED_AT_KEY]
  const lastPromptedAt = lastPromptedRaw ? Number(lastPromptedRaw) : null

  return {
    rated: map[RATED_KEY] === '1',
    // Guard against a corrupted/NaN timestamp, which would otherwise make
    // shouldPrompt() permanently return false. Fall back to null (prompt-eligible).
    lastPromptedAt: lastPromptedAt !== null && Number.isFinite(lastPromptedAt) ? lastPromptedAt : null
  }
}

export const markRated = async (): Promise<void> => {
  await AsyncStorage.setItem(RATED_KEY, '1')
}

export const markPrompted = async (now: number): Promise<void> => {
  await AsyncStorage.setItem(LAST_PROMPTED_AT_KEY, String(now))
}

export const shouldPrompt = (state: ReviewState, now: number): boolean => {
  if (state.rated) {
    return false
  }
  if (state.lastPromptedAt === null) {
    return true
  }
  return now - state.lastPromptedAt >= PROMPT_INTERVAL_MS
}

export const requestNativeReview = async (): Promise<void> => {
  if (await StoreReview.hasAction()) {
    await StoreReview.requestReview()
    return
  }

  if (Platform.OS === 'android') {
    // market:// opens the Play Store app directly, but rejects on devices without
    // it (or where the scheme is blocked); fall back to the https listing.
    try {
      await Linking.openURL(PLAY_STORE_MARKET_URL)
    } catch {
      await Linking.openURL(PLAY_STORE_WEB_URL)
    }
    return
  }

  await Linking.openURL(APP_STORE_REVIEW_URL)
}
